// PR-12.4 — JoinTeam page rewritten to support both:
//   - New flow: team_invitations row → accept_team_invitation() RPC →
//     INSERT team_members. Race-condition safe via FOR UPDATE lock.
//   - Legacy flow: teams.invite_token UPDATE pattern. Backward-compat for
//     teams created before this PR landed.
//
// The page picks the right flow by checking team_invitations FIRST. If a
// matching token is found there, it routes to the new RPC; otherwise
// falls back to the legacy `teams` table query.
//
// PR-AY1 §1.2 fix retained: `if (!invite)` covers both null + undefined
// for react-query error states.
//
// TASK-67 PR-D: dropped player1_id / player2_id reads + writes in the
// new-invite path. The inviter name now resolves via team_members
// (role='primary', oldest joined_at) instead of teams.player1_id. The
// legacy fallback path is preserved verbatim for backward-compat with
// pre-PR-12.4 invite tokens — once teams.player1_id / player2_id are
// dropped in PR-E that branch becomes unreachable (the legacy SELECT
// will 400 and the page falls through to the "Inbjudan hittades inte"
// state). Live legacy invite-link traffic at PR-D ship is expected to be
// near-zero per the investigation.

import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Trophy, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

// TASK-66 PR-D: renamed `captainName` → `inviterName`. The captain
// concept is gone — the field now holds the player1 (or first known
// roster member) display name shown as "X vill spela med dig i Y".
type ResolvedInvite =
  | {
      mode: "new";
      teamId: string;
      teamName: string | null;
      leagueName: string | null;
      inviterName: string;
      intendedRole: "primary" | "reserve";
    }
  | {
      mode: "legacy";
      teamId: string;
      teamName: string | null;
      leagueName: string | null;
      inviterName: string;
      alreadyAccepted: boolean;
    };

const JoinTeam = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: invite, isLoading } = useQuery<ResolvedInvite | null>({
    queryKey: ["join-team", token],
    queryFn: async () => {
      // 1. Try the new team_invitations table first (PR-12.4 path)
      // TASK-67 PR-D: dropped the teams.player1_id read from the embed.
      // Inviter name now resolves via team_members (role='primary', oldest
      // joined_at) — falls back to "Din medspelare" when the team has no
      // primary member yet (shouldn't happen since the inviter row gets
      // created with the team).
      const { data: invRow } = await supabase
        .from("team_invitations")
        .select(
          "id, team_id, intended_role, status, expires_at, invited_by_user_id, teams!inner(id, team_name, league_id, leagues(name))",
        )
        .eq("invite_token", token!)
        .maybeSingle();

      if (invRow) {
        if (invRow.status !== "pending") return null;
        if (new Date(invRow.expires_at) < new Date()) return null;
        const team = invRow.teams as unknown as {
          id: string;
          team_name: string | null;
          league_id: string;
          leagues: { name: string } | null;
        };
        // TASK-67 PR-D: inviter name resolution — prefer the explicit
        // invited_by_user_id on the invitation (always set per the
        // team_invitations schema NOT NULL constraint). Falls back to the
        // first primary member (oldest joined_at) only as a defensive
        // backstop.
        let inviterName = "Din medspelare";
        const inviterUserId = (invRow as { invited_by_user_id?: string | null }).invited_by_user_id ?? null;
        if (inviterUserId) {
          const { data: p1 } = await supabase
            .from("public_profiles" as any)
            .select("full_name")
            .eq("id", inviterUserId)
            .maybeSingle();
          if (p1?.full_name) inviterName = p1.full_name;
        } else {
          // Defensive fallback: oldest primary member of the team. Sprint 4
          // PR 4.1: dropped .not("user_id", "is", null) filter so customer-only
          // primaries also qualify as inviter-fallback. Display name resolves
          // via either profile (user_id path) or customer (customer_id path).
          const { data: members } = await supabase
            .from("team_members")
            .select(
              `user_id, customer_id, joined_at,
               profile:profiles!team_members_user_id_fkey(full_name),
               customer:customers!team_members_customer_id_fkey(full_name, email)`,
            )
            .eq("team_id", team.id)
            .eq("role", "primary")
            .order("joined_at", { ascending: true })
            .limit(1);
          const first = members?.[0] as
            | {
                user_id: string | null;
                customer_id: string | null;
                profile: { full_name: string | null } | null;
                customer: { full_name: string | null; email: string | null } | null;
              }
            | undefined;
          if (first) {
            const name =
              first.profile?.full_name?.trim() ||
              first.customer?.full_name?.trim() ||
              first.customer?.email?.trim();
            if (name) inviterName = name;
          }
        }
        return {
          mode: "new",
          teamId: team.id,
          teamName: team.team_name,
          leagueName: team.leagues?.name ?? null,
          inviterName,
          intendedRole: invRow.intended_role as "primary" | "reserve",
        };
      }

      // TASK-67 PR-E: removed the pre-UPPDRAG-12.4 legacy fallback that
      // read teams.player1_id / player2_id / invite_token + UPDATE'd
      // teams.player2_id. Those columns are gone (migration
      // 20260504121241), so the SELECT would have errored 400 on missing
      // columns. team_invitations is now the SOLE source of truth for
      // invite resolution. Any cached pre-UPPDRAG-12.4 invite link will
      // simply fall through to the "Inbjudan hittades inte" state — same
      // user-facing outcome the dead-code branch would have delivered
      // post-PR-E, but with no PostgREST 400 error in the network tab.
      return null;
    },
    enabled: !!token,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!invite || !user) throw new Error("not_ready");

      if (invite.mode === "new") {
        // PR-12.4: race-condition-safe accept via RPC. Returns the new
        // team_members.id (we don't use it but it confirms the write).
        const { error } = await supabase.rpc("accept_team_invitation", {
          p_token: token!,
          p_user_id: user.id,
        });
        if (error) {
          // Map RPC errors to user-friendly Swedish messages
          const msg = error.message || "";
          if (msg.includes("invitation_not_found"))
            throw new Error("Inbjudan hittades inte");
          if (msg.includes("invitation_not_pending"))
            throw new Error(
              "Inbjudan har redan använts eller återkallats",
            );
          if (msg.includes("invitation_expired"))
            throw new Error("Inbjudan har gått ut");
          if (msg.includes("roster_full"))
            throw new Error("Laget är fullt");
          if (msg.includes("23505") || msg.includes("duplicate"))
            throw new Error("Du är redan med i laget");
          throw error;
        }
      }
      // TASK-67 PR-E: the legacy `else` branch (UPDATE teams.player2_id
      // + invite_token=null) was removed alongside the legacy fallback
      // SELECT in the loader. Both wrote the dropped-in-PR-E columns and
      // are unreachable now (loader returns null for any cached pre-
      // UPPDRAG-12.4 link → page renders the "not found" state → accept
      // mutation never fires for mode='legacy'). The accept flow is now
      // exclusively the team_invitations RPC path above.
    },
    onSuccess: () => {
      toast.success("Du är nu med i laget!");
      navigate("/leagues");
    },
    onError: (err: Error) =>
      toast.error(err.message || "Kunde inte acceptera inbjudan"),
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Laddar inbjudan...</p>
        </div>
      );
    }

    // PR-AY1 §1.2: covers null AND undefined (react-query error state)
    if (!invite) {
      return (
        <div className="flex flex-col items-center gap-2">
          <XCircle className="h-8 w-8 text-destructive" />
          <p className="text-muted-foreground">
            Inbjudan hittades inte eller har redan använts
          </p>
          <Link to="/leagues">
            <Button variant="outline" className="mt-2">
              Gå till serier
            </Button>
          </Link>
        </div>
      );
    }

    // Legacy already-accepted check (the `new` flow already filters
    // status='pending' upstream so this only fires for legacy teams)
    if (invite.mode === "legacy" && invite.alreadyAccepted) {
      return (
        <div className="flex flex-col items-center gap-2">
          <CheckCircle2 className="h-8 w-8 text-green-500" />
          <p className="text-muted-foreground">
            Inbjudan har redan accepterats
          </p>
          <Link to="/leagues">
            <Button variant="outline" className="mt-2">
              Gå till serier
            </Button>
          </Link>
        </div>
      );
    }

    if (!user) {
      return (
        <div className="flex flex-col items-center gap-3">
          <p className="text-muted-foreground">
            Logga in eller skapa konto för att acceptera inbjudan
          </p>
          <Link to={`/auth?redirect=/join-team/${token}`}>
            <Button className="w-full">Logga in</Button>
          </Link>
        </div>
      );
    }

    const leagueName = invite.leagueName || "serien";

    return (
      <div className="flex flex-col items-center gap-3">
        <p className="text-lg">
          <span className="font-semibold">{invite.inviterName}</span> vill
          spela med dig i <span className="font-semibold">{leagueName}</span>
        </p>
        {invite.teamName && (
          <p className="text-muted-foreground">Lagnamn: {invite.teamName}</p>
        )}
        {invite.mode === "new" && invite.intendedRole === "reserve" && (
          <p className="text-sm text-muted-foreground">
            Roll: <strong>Reserv</strong> (du blir med på bänken)
          </p>
        )}
        <Button
          className="w-full mt-2"
          onClick={() => acceptMutation.mutate()}
          disabled={acceptMutation.isPending}
        >
          {acceptMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Accepterar...
            </>
          ) : (
            "Acceptera och gå med"
          )}
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Trophy className="h-10 w-10 mx-auto text-primary mb-2" />
          <CardTitle>Inbjudan till seriespel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinTeam;
